import {
  deserialize,
  DrupalNode,
  DrupalSearchApiFacet,
} from 'next-drupal';

import {createRef, useContext, useEffect, useRef, useState} from "react";
import {SearchHero} from "../../organisms/hero/search-hero";
import {getKvpPair} from "../../../lib/get-kvp-pair";
import {searchIndex} from "../../../lib/search-index";
import {Dictionary, DrupalSearchApiResponseMeta} from "../../../types/hygiena-types";
import {useRouter} from "next/router";
import {DrupalJsonApiParams} from "drupal-jsonapi-params";
import dynamic from "next/dynamic";
import {DictionaryContext} from "../../../context/dictionary-context";

const Button = dynamic(() => import("../../atoms/button").then((mod) => mod.Button));
const LoadingSpinnerOverlay = dynamic(() => import("../../atoms/loading-spinner").then((mod) => mod.LoadingSpinnerOverlay));
const Pagination = dynamic(() => import("../../organisms/widget--pagination").then((mod) => mod.Pagination));
const NodeArticleTeaser = dynamic(() => import("components/templates/nodes/node--article--teaser").then((mod) => mod.NodeArticleTeaser));
const SectionHeading = dynamic(() => import("../../organisms/section-heading"));

interface ArticlesPageProps {
  node: DrupalNode,
  articles?: DrupalNode[];
  latestArticle?: DrupalNode,
  meta?: DrupalSearchApiResponseMeta,
  facets?: DrupalSearchApiFacet[];
}

export function NewsPage({ node, articles, latestArticle, meta, facets: initialFacets }: ArticlesPageProps) {
  const ready = useRef(false);
  const router = useRouter();
  const paginationScrollRef = createRef<HTMLDivElement>();
  const [documentCount, setDocumentCount] = useState(meta?.count ?? 0);
  const [page, setPage] = useState(0);
  const [results, setResults] = useState<DrupalNode[]>(articles ?? [])
  const [facets, setFacets] = useState<DrupalSearchApiFacet[]>(initialFacets ?? [])
  const [articleType, setArticleType] = useState("");
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState<boolean>();
  const t = useContext<Dictionary>(DictionaryContext);
  const pageLimit = 10;

  const heroHeadline = getKvpPair(node, "hero_headline")?.value;
  const searchPlaceholder = getKvpPair(node, "search_placeholder")?.value;
  const readMoreText = getKvpPair(node, "read_more")?.value ?? `${t?.all?.read_more}`;
  const allFacetText = getKvpPair(node, "all_facet")?.value ?? `${t?.all?.all}`;

  function getParams() {
    const params = new DrupalJsonApiParams()
      .addFilter("langcode", router?.locale ?? "en")
      .addFilter("status", "1")
      .addPageLimit(12)
      .addPageOffset(pageLimit * page)
      .addInclude([
        "field_article_image.image",
        "field_article_type",
      ])
      .addSort("field_article_date", "DESC");
    if (latestArticle?.id) params.addFilter("uuid", latestArticle.id, "<>");
    if (articleType?.length) params.addFilter("field_article_type", articleType)
    if (searchText?.length) params.addFilter("fulltext", searchText);

    return params.getQueryString();
  }

  async function updateFacets() {
    let response;
    let results;
    const params = getParams();
    try {
      response = await searchIndex("articles", params, router.locale, router.defaultLocale);
      response = response.ok ? response.data : {};
      results = response?.data ? await deserialize(response) as DrupalNode[] : [];
    }
    catch (error) {
    }

    results = results ?.length > 10 ? results.slice(0, 10) : results;
    setResults(results);
    setDocumentCount(response?.meta?.count);

    if (results?.length) {
      setFacets(response.meta.facets)
    }
    setLoading(false);
  }

  useEffect(() => {
    if (!ready?.current) {
      ready.current = true;
      return;
    }
    if (typeof window !== undefined && ready?.current === true) {
      setLoading(true);
      updateFacets();
    }
  }, [page, articleType, searchText, router.locale]);

  function searchCallback(text) {
    setLoading(true);
    setSearchText(text);
  }

  async function changePage(page) {
    setLoading(true);
    await setPage(page);
  }

  return (
    <>
      <SearchHero title={node?.title} image={node?.field_kvp_hero_image} small={true} headline={heroHeadline} searchPlaceholder={searchPlaceholder} searchCallback={searchCallback}/>
      <div className="container pb-10 mx-auto scroll-m-20" ref={paginationScrollRef}>
        <SectionHeading title={t?.all?.latest_news ?? "Latest News"}/>
        {latestArticle && (
          <div className="mb-12">
            <NodeArticleTeaser key={latestArticle.id} node={latestArticle} fullWidth={true} readMoreText={readMoreText}/>
          </div>
        )}
        <div className="order-first md:order-last md:top-0 pt-8">
          {facets.map((facet, key) => {
            if (facet?.terms?.length) {
              return (
                <div key={key}>
                  <div className="text-center text-black pb-8">
                    <div className="text-center flex flex-wrap gap-2 justify-start md:justify-end">
                      <Button
                        invert={!!(articleType && articleType.length > 0)}
                        className={"font-normal !px-4"}
                        color={!articleType ? "primary" : "black"}
                        callback={() => {setPage(0); setArticleType("")}}>{allFacetText}</Button>
                      {facet.terms.map(term => (
                        <Button
                          key={term.values.value}
                          invert={term.values.value !== articleType}
                          className={"font-normal !px-4"}
                          color={term.values.value === articleType ? "primary" : "black"}
                          callback={() => {setPage(0); setArticleType(term.values.value)}}>{term.values.label}</Button>
                      ))}
                    </div>
                  </div>
                </div>
              )
            }
          })}
        </div>
        <div className="relative">
          {results?.length ? (
            <>
              <LoadingSpinnerOverlay loading={loading} fixed={true}/>
              <div className="grid gap-6 grid-cols-1 md:grid-cols-2 order-last md:order-first">
                {results.map((article, index) => (
                  <NodeArticleTeaser key={article.id} node={article} fullWidth={false} readMoreText={readMoreText}/>
                ))}
              </div>
            </>
          ) : (
            <p className="text-center text-2xl xl:text-3xl font-bold">{`${t?.all?.news_no_results}`}</p>
          )}
        </div>
        {results?.length ? (
          <div className="px-6 lg:px-0">
            <Pagination count={documentCount} page={page} limit={pageLimit} changePage={changePage} radius={8} scrollRef={paginationScrollRef}/>
          </div>
        ): <></>}
      </div>
    </>
  );
}